/**
 * Domain Builder Component
 *
 * Chat-driven card sequence for building each domain.
 * Uses existing card repository and chat thread system.
 * Integrates with automatic chat thread generation (ISSUE-158).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { message } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { ChatOperationalLayout } from '../ChatOperational/ChatOperationalLayout';
import { useBridgeSession } from '../../contexts/BridgeSessionContext';
import { useChat } from '../../contexts/ChatContext';
import { bridgeSessionService } from '../../services/bridgeSessionService';
import styles from './DomainBuilder.module.css';

export const DomainBuilder: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const {
    getDomainState,
    getSubdomainState,
    sessionState,
    setSessionState,
    selectedDomainId,
    selectedSubdomainId,
    navigateToDomain,
  } = useBridgeSession();
  const { createThread, selectThread, threads, currentThreadId } = useChat();
  const [headerExpanded, setHeaderExpanded] = useState(false);

  // Auto-select first in-progress/locked domain when none selected from sidebar
  useEffect(() => {
    if (!sessionState || selectedDomainId) return;

    for (const [domainId, domain] of Object.entries(sessionState.domains)) {
      if (domain.state === 'in_progress' || domain.state === 'locked') {
        const inProgressSubdomain = domain.subdomains.find((sub) => sub.state === 'in_progress');
        navigateToDomain(domainId, inProgressSubdomain?.artifact_item_id);
        return;
      }
    }
  }, [sessionState, selectedDomainId, navigateToDomain]);

  const currentDomain = selectedDomainId;
  const currentSubdomain = selectedSubdomainId;

  // Create or load thread for current domain
  useEffect(() => {
    if (!sessionId || !currentDomain) return;

    const threadName = `bridge-session-${sessionId}-domain-${currentDomain}`;

    const existingThread = threads.find((t) => t.name === threadName);

    if (existingThread) {
      if (currentThreadId !== existingThread.id) {
        selectThread(existingThread.id);
      }
    } else {
      createThread(
        threadName,
        `Bridge Session: ${currentDomain} domain`,
        undefined,
        undefined,
        {
          bridge_session_id: sessionId,
          step: 'domain_builder',
          domain_id: currentDomain,
        }
      );
    }
  }, [sessionId, currentDomain, threads, currentThreadId, createThread, selectThread]);

  const handleDomainComplete = useCallback(async () => {
    if (!sessionId || !currentDomain) return;

    try {
      const updated = await bridgeSessionService.markDomainComplete(sessionId, currentDomain);
      setSessionState(updated);
    } catch (error: unknown) {
      console.error('Failed to mark domain as complete:', error);
      message.error('Could not save domain completion. Please try again.');
    }
  }, [sessionId, currentDomain, setSessionState]);

  const onCardAction = useCallback(
    (action: string, _payload: unknown) => {
      if (action === 'domain_complete') {
        void handleDomainComplete();
      }
    },
    [handleDomainComplete]
  );

  const domain = currentDomain ? getDomainState(currentDomain) : null;
  const subdomain =
    currentDomain && currentSubdomain
      ? getSubdomainState(currentDomain, currentSubdomain)
      : null;
  const domainName = domain?.name || currentDomain || 'Domain';
  const statusLabel =
    domain?.state === 'sufficient'
      ? 'Complete'
      : domain?.state === 'in_progress'
        ? 'In Progress'
        : 'Locked';

  return (
    <div className={styles.domainBuilder}>
      <div className={styles.domainHeader}>
        <button
          type="button"
          className={styles.headerToggle}
          onClick={() => setHeaderExpanded((v) => !v)}
          aria-expanded={headerExpanded}
        >
          <span className={styles.headerTitle}>
            Building: {domainName}
            {subdomain && <span className={styles.subdomainBadge}>{subdomain.name}</span>}
          </span>
          <span className={styles.headerMeta}>
            <span className={styles.statusPill}>{statusLabel}</span>
            {headerExpanded ? <UpOutlined /> : <DownOutlined />}
          </span>
        </button>
        {headerExpanded && (
          <div className={styles.headerDetails}>
            <p className={styles.domainDescription}>
              Use the chat to define this domain. Cards appear as you provide information.
            </p>
            {domain && domain.subdomains.length > 0 && (
              <p className={styles.subdomainInfo}>
                {domain.subdomains.length} subdomain{domain.subdomains.length !== 1 ? 's' : ''}{' '}
                configured
              </p>
            )}
          </div>
        )}
      </div>

      <div className={styles.chatContainer}>
        <ChatOperationalLayout
          showHeader={false}
          hideNextActionPanel
          onCardAction={onCardAction}
        />
      </div>
    </div>
  );
};
